"use client";

import { useState, useEffect } from "react";
import Link from "next/link";

const STORAGE_KEY = "bpderma_cookie_consent";

export interface ConsentState {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

function loadConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentState;
  } catch {
    return null;
  }
}

function saveConsent(consent: ConsentState) {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  window.dispatchEvent(new CustomEvent("cookieConsentUpdated", { detail: consent }));
}

interface CookieConsentProps {
  locale: string;
}

export default function CookieConsent({ locale }: CookieConsentProps) {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = loadConsent();
    if (!existing) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true, timestamp: Date.now() });
    setVisible(false);
    setShowPreferences(false);
  };
  const savePreferences = () => {
    saveConsent({ necessary: true, analytics, marketing, timestamp: Date.now() });
    setVisible(false);
    setShowPreferences(false);
  };
  const rejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false, timestamp: Date.now() });
    setVisible(false);
    setShowPreferences(false);
  };

  if (!visible) return null;

  return (
    <>
      {!showPreferences && (
        <div
          role="region"
          aria-label="Cookie consent"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 200,
            padding: "16px var(--bp-edge)",
          }}
        >
          <div
            style={{
              maxWidth: 1080,
              margin: "0 auto",
              background: "var(--bp-ink)",
              color: "var(--bp-bg)",
              padding: "20px 24px",
              border: "1px solid var(--bp-ink)",
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 24,
              alignItems: "center",
            }}
            className="bp-cookie-bar"
          >
            <div>
              <div className="bp-mono-sm" style={{ color: "oklch(0.72 0 0)", marginBottom: 6 }}>
                COOKIES · 1223/2009 · GDPR
              </div>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: "oklch(0.85 0 0)" }}>
                We use functional cookies for the cart and session, and optional cookies for
                analytics. Read the{" "}
                <Link
                  href={`/${locale}/cookie-policy`}
                  style={{ color: "var(--bp-bg)", borderBottom: "1px solid currentColor" }}
                >
                  Cookie Policy
                </Link>
                .
              </p>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button
                onClick={() => setShowPreferences(true)}
                className="bp-mono-sm"
                style={{
                  padding: "10px 14px",
                  border: "1px solid oklch(0.45 0 0)",
                  color: "oklch(0.85 0 0)",
                  background: "none",
                  cursor: "pointer",
                }}
              >
                MANAGE
              </button>
              <button
                onClick={rejectAll}
                className="bp-mono-sm"
                style={{
                  padding: "10px 14px",
                  border: "1px solid oklch(0.45 0 0)",
                  color: "oklch(0.85 0 0)",
                  background: "none",
                  cursor: "pointer",
                }}
              >
                REJECT
              </button>
              <button
                onClick={acceptAll}
                className="bp-mono-sm"
                style={{
                  padding: "10px 18px",
                  border: "1px solid var(--bp-bg)",
                  color: "var(--bp-ink)",
                  background: "var(--bp-bg)",
                  cursor: "pointer",
                }}
              >
                ACCEPT ALL
              </button>
            </div>
          </div>
          <style>{`
            @media (max-width: 700px) {
              .bp-cookie-bar { grid-template-columns: 1fr !important; }
            }
          `}</style>
        </div>
      )}

      {showPreferences && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Cookie preferences"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={() => setShowPreferences(false)}
            style={{ position: "absolute", inset: 0, background: "oklch(0.10 0 0 / 0.5)" }}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 540,
              background: "var(--bp-bg)",
              border: "1px solid var(--bp-ink)",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid var(--bp-ink)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "var(--bp-ink)",
                color: "var(--bp-bg)",
              }}
            >
              <div className="bp-mono-sm" style={{ color: "var(--bp-bg)", letterSpacing: "0.18em" }}>
                COOKIE PREFERENCES
              </div>
              <button
                onClick={() => setShowPreferences(false)}
                className="bp-mono-sm"
                style={{
                  color: "var(--bp-bg)",
                  background: "none",
                  border: 0,
                  cursor: "pointer",
                }}
              >
                CLOSE ×
              </button>
            </div>

            <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
              <PreferenceRow
                label="Necessary"
                pinned
                checked
                onChange={() => {}}
                desc="Required for the website to function. Includes session, cart, and security tokens. Cannot be disabled."
              />
              <PreferenceRow
                label="Analytics"
                checked={analytics}
                onChange={setAnalytics}
                desc="Helps us understand how visitors use the site. Data is anonymised and never sold."
              />
              <PreferenceRow
                label="Marketing"
                checked={marketing}
                onChange={setMarketing}
                desc="Used to measure campaign effectiveness. We do not share your data with ad networks without consent."
              />
            </div>

            <div
              style={{
                padding: 24,
                borderTop: "1px solid var(--bp-rule)",
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <button onClick={rejectAll} className="bp-btn-ghost" style={{ flex: 1 }}>
                REJECT
              </button>
              <button onClick={savePreferences} className="bp-btn-ghost" style={{ flex: 1 }}>
                SAVE
              </button>
              <button onClick={acceptAll} className="bp-btn" style={{ flex: 1 }}>
                ACCEPT ALL
              </button>
            </div>

            <p className="bp-mono-sm" style={{ padding: "0 24px 20px", textAlign: "center", margin: 0 }}>
              CHANGE ANY TIME ·{" "}
              <Link
                href={`/${locale}/cookie-policy`}
                onClick={() => {
                  setShowPreferences(false);
                  setVisible(false);
                }}
                style={{
                  color: "var(--bp-ink)",
                  borderBottom: "1px solid currentColor",
                }}
              >
                COOKIE POLICY
              </Link>
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function PreferenceRow({
  label,
  desc,
  checked,
  onChange,
  pinned,
}: {
  label: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  pinned?: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 16,
        padding: "16px 0",
        borderBottom: "1px solid var(--bp-rule)",
      }}
    >
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <span
            style={{
              fontFamily: "var(--bp-mono)",
              fontSize: 12,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--bp-ink)",
            }}
          >
            {label}
          </span>
          {pinned && (
            <span className="bp-mono-sm" style={{ color: "var(--bp-ink-3)" }}>
              ALWAYS ON
            </span>
          )}
        </div>
        <p style={{ margin: 0, fontSize: 13, color: "var(--bp-ink-2)", lineHeight: 1.55 }}>
          {desc}
        </p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        disabled={pinned}
        onClick={() => !pinned && onChange(!checked)}
        style={{
          width: 44,
          height: 24,
          background: checked ? "var(--bp-ink)" : "var(--bp-bg)",
          border: "1px solid var(--bp-ink)",
          position: "relative",
          cursor: pinned ? "not-allowed" : "pointer",
          padding: 0,
          opacity: pinned ? 0.6 : 1,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: checked ? 22 : 2,
            width: 18,
            height: 18,
            background: checked ? "var(--bp-bg)" : "var(--bp-ink)",
            transition: "left 160ms",
          }}
        />
      </button>
    </div>
  );
}
